import { useCallback } from 'react';
import { API_URL } from '../Config';

export function useAI(projectId, setTimelineItems, setIsLoading, setMessage, selectedLanguage) {

  const handleAIGenScript = useCallback(async (selectedLanguage) => {
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    try {
      console.log("Initiating AI script generation for project:", projectId);
      console.log("Selected language for AI script generation:", selectedLanguage);
      const response = await fetch(`${API_URL}/projects/${projectId}/analyze${selectedLanguage && selectedLanguage !== 'original' ? `?target_language_code=${selectedLanguage}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      console.log("AI script generation complete:", result);
      // After analysis, re-fetch timeline items to update UI
      const timelineResponse = await fetch(`${API_URL}/projects/${projectId}/timeline-items`);
      if (!timelineResponse.ok) {
        throw new Error(`HTTP error! status: ${timelineResponse.status}`);
      }
      const timelineData = await timelineResponse.json();
      setTimelineItems(timelineData);
      setMessage({ text: 'AI script generation complete!', type: 'success' });
    } catch (error) {
      console.error("Error during AI script generation:", error);
      setMessage({ text: `AI script generation failed: ${error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, setTimelineItems, setIsLoading, setMessage, selectedLanguage]);

  const handleAIGenVoice = useCallback(async (voiceModel, itemId = null, variation = 1) => {
    setIsLoading(true);
    setMessage({ text: '', type: '' });

    let url = '';
    if (itemId) {
      // URL for single item regeneration
      url = `${API_URL}/timeline-items/${itemId}/generate-audio?voice_model=${voiceModel}&variation=${variation}${selectedLanguage && selectedLanguage !== 'original' ? `&target_language_code=${selectedLanguage}` : ''}`;
    } else {
      // URL for bulk generation for the whole project
      url = `${API_URL}/projects/${projectId}/generate-audio?voice_model=${voiceModel}${selectedLanguage && selectedLanguage !== 'original' ? `&target_language_code=${selectedLanguage}` : ''}`;
    }

    try {
      console.log(`Initiating AI voice generation for ${itemId ? `item ${itemId}` : `project ${projectId}`}`);
      console.log(`Selected language: ${selectedLanguage}`);
      console.log(`Final URL: ${url}`);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("AI voice generation complete:", result);

      // After generation, re-fetch all timeline items to update the UI
      const timelineResponse = await fetch(`${API_URL}/projects/${projectId}/timeline-items`);
      if (!timelineResponse.ok) {
        throw new Error(`HTTP error! status: ${timelineResponse.status}`);
      }
      const timelineData = await timelineResponse.json();
      setTimelineItems(timelineData);

      setMessage({ text: 'AI voice generation complete!', type: 'success' });
    } catch (error) {
      console.error("Error during AI voice generation:", error);
      setMessage({ text: `AI voice generation failed: ${error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, setTimelineItems, setIsLoading, setMessage, selectedLanguage]);



  const handleRegenScript = useCallback(async (itemId) => {
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    try {
      console.log("Regenerating script for timeline item:", itemId);
      const response = await fetch(`${API_URL}/timeline-items/${itemId}/regenerate-script${selectedLanguage && selectedLanguage !== 'original' ? `?target_language_code=${selectedLanguage}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      console.log("Script regeneration complete:", result);

      // After regeneration, re-fetch timeline items to update UI
      const timelineResponse = await fetch(`${API_URL}/projects/${projectId}/timeline-items`);
      if (!timelineResponse.ok) {
        throw new Error(`HTTP error! status: ${timelineResponse.status}`);
      }
      const timelineData = await timelineResponse.json();
      setTimelineItems(timelineData);

      setMessage({ text: 'Script regenerated successfully!', type: 'success' });
    } catch (error) {
      console.error("Error regenerating script:", error);
      setMessage({ text: `Script regeneration failed: ${error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, setTimelineItems, setIsLoading, setMessage, selectedLanguage]);

  const handleRemoveAudio = useCallback(async () => {
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    try {
      console.log(`Removing audio for project ${projectId} in language: ${selectedLanguage}`);
      const url = selectedLanguage === 'original' 
        ? `${API_URL}/projects/${projectId}/audio`
        : `${API_URL}/projects/${projectId}/audio?language_code=${selectedLanguage}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      console.log("Audio removal complete:", result);

      // After removal, re-fetch timeline items to update UI
      const timelineResponse = await fetch(`${API_URL}/projects/${projectId}/timeline-items`);
      if (!timelineResponse.ok) {
        throw new Error(`HTTP error! status: ${timelineResponse.status}`);
      }
      const timelineData = await timelineResponse.json();
      setTimelineItems(timelineData);

      const languageName = selectedLanguage === 'original' ? 'original' : selectedLanguage;
      setMessage({ text: `Audio removed successfully for ${languageName}! ${result.files_deleted} files deleted.`, type: 'success' });
    } catch (error) {
      console.error("Error removing audio:", error);
      setMessage({ text: `Failed to remove audio: ${error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, setTimelineItems, setIsLoading, setMessage, selectedLanguage]);


  return { handleAIGenScript, handleAIGenVoice, handleRegenScript, handleRemoveAudio };
}